import { Grid, Typography } from '@material-ui/core';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HandleCashOfficeActions } from 'src/cash-office-display/business-logic/handle-cash-office-actions';
import {
  getPaidAccounts,
  PaidAccountType,
} from 'src/cash-office-display/business-logic/services/get-paid-accounts';
import BaseButton from 'src/common/components/base-button';
import { setHomePage } from 'src/common/components/emerald-home-page/emerald-home-page';
import SelectableTable, { IRowData } from 'src/common/components/selectable-table/selectable-table';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import Pagination from 'src/pagination/pagination';
import CashOfficeTitle from '../cash-office-title';
import { styles } from './styles';

const TABLE_SIZE = 7; // Lucky number 7

type AccountRow = IRowData & { taxable: boolean }; // Apparently using a type is fine if it's a union

interface SelectedAccount {
  name: string;
  accountId: string;
  taxable: boolean;
}

export const displayPaidAccountSelectionView = (accountLookupType: PaidAccountType) => {
  setHomePage(<PaidAccountSelection accountLookupType={accountLookupType} />);
};

const PaidAccountSelection: React.FC<PaidAccountProps> = ({ accountLookupType }): JSX.Element => {
  const classes = styles();

  const [accountList, setAccountList] = useState<AccountRow[]>([]);
  const [selected, setSelected] = useState<SelectedAccount>({
    name: '',
    accountId: '',
    taxable: false,
  });
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [lastPage, setLastPage] = useState<number>(0);
  const { t } = useTranslation();

  const onSelection = useCallback(
    (selectedElements: string[]) => {
      const [firstElement] = selectedElements;
      const account = accountList.find(acct => acct.id === firstElement);
      if (account) {
        setSelected({ name: account.rootCell, accountId: account.id, taxable: account.taxable });
      } else {
        setSelected({ name: '', accountId: '', taxable: false });
      }
    },
    [accountList],
  );

  const updateCurrentPage = useCallback((newPage: number) => {
    setCurrentPage(newPage - 1);
  }, []);

  useEffect(() => {
    let mounted = true;
    getPaidAccounts(accountLookupType)
      .then(accounts => {
        if (mounted) {
          setAccountList(
            accounts.map(a => ({
              id: a.externalId,
              taxable: a.profileIsTaxable,
              rootCell: a.name,
              subcellOne: '',
              subcellTwo: '',
            })),
          );

          setLastPage(Math.ceil(accounts.length / TABLE_SIZE));
        }
      })
      .catch(e => {
        popupMessage(e);
      });

    return () => {
      mounted = false;
    };
  }, [accountLookupType]);

  return (
    <Grid item className={`fullHeight flexFull`} style={{ width: '0' }}>
      <Grid
        item
        xs={12}
        className={`${classes.background} ${classes.accountSelectionContainer} fullWidth fullHeight`}
      >
        <Grid container className={'fullHeight'} data-testid='account-selection-details-container'>
          <Grid item container direction='column' className={'fullHeight'}>
            <Grid container className={classes.background}>
              <CashOfficeTitle
                title={t(
                  accountLookupType === PaidAccountType.PaidOut
                    ? 'PaidOutCommand'
                    : 'PaidInCommand',
                )}
              />
            </Grid>
            <Grid container className={classes.background}>
              <Typography
                className={classes.instructionTypography}
                data-testid='cash-office-title-text'
              >
                {t('SelectAnAccount')}
              </Typography>
            </Grid>
            <Grid container className={classes.background}>
              <Grid item xs={6}>
                <Typography
                  className={classes.tenderListHeader}
                  data-testid='cash-office-table-title-text'
                >
                  {t('AccountName')}
                </Typography>
              </Grid>
            </Grid>
            <Grid container className={classes.tenderListContainer}>
              <SelectableTable
                tableData={accountList}
                onSelection={onSelection}
                rowsPerPage={TABLE_SIZE}
                currentPage={currentPage}
                showSelectedAdornment={true}
              />
            </Grid>
            <Grid
              container
              className={`${classes.background}`}
              direction='row'
              alignItems='center'
              justifyContent='flex-start'
            >
              <Grid item xs={4}>
                <Grid container style={{ justifyContent: 'flex-start' }}>
                  <Pagination
                    currentPage={lastPage ? currentPage + 1 : 0}
                    totalPages={lastPage}
                    updateCurrentPageHandler={updateCurrentPage}
                    testIdPrefix={'paid-account'}
                  />
                </Grid>
              </Grid>
              <Grid
                item
                container
                direction='row'
                alignItems='center'
                justifyContent='center'
                xs={4}
              >
                <BaseButton
                  buttonType='white'
                  className={classes.cancelButton}
                  onClick={HandleCashOfficeActions.hidePaidAccountSelectionView}
                  data-testid={'account-selection-cancel-button'}
                >
                  {t('Cancel')}
                </BaseButton>
                <BaseButton
                  className={classes.okButton}
                  disabled={!selected.accountId}
                  onClick={() =>
                    accountLookupType === PaidAccountType.PaidOut
                      ? HandleCashOfficeActions.openPaidOutActionWindow(
                          selected.name,
                          selected.accountId,
                        )
                      : HandleCashOfficeActions.openPaidInActionWindow(
                          selected.name,
                          selected.accountId,
                          selected.taxable,
                        )
                  }
                  data-testid={'account-selection-accept-button'}
                >
                  {t('Ok')}
                </BaseButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

interface PaidAccountProps {
  accountLookupType: PaidAccountType;
}

export default PaidAccountSelection;
